// noinspection JSUnusedGlobalSymbols
import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { of, switchMap } from 'rxjs';
import { catchError, filter, map, withLatestFrom } from 'rxjs/operators';

import { InvalidApiVersionError, InvalidIdentifierError } from 'app/shared/models';
import { ApiConfigStorageService, TerminalClient } from 'app/shared/services';
import { isValidApiVersion, isValidIdentifier } from 'app/shared/utils';
import { selectApiConfigState, selectIdentifier } from 'app/state/api-config/api-config.selectors';

import {
    preloadApiConfig,
    preloadApiConfigSuccess,
    refreshApiVersion,
    refreshApiVersionFailure,
    refreshApiVersionSuccess,
    registerApiConfig,
    registerApiConfigFailure,
    registerApiConfigSuccess,
    resetApiConfig,
} from './api-config.actions';

// noinspection JSUnusedGlobalSymbols
@Injectable()
export class ApiConfigEffects {
    preloadApiConfig$ = createEffect(() =>
        this.actions$.pipe(
            ofType(preloadApiConfig),
            withLatestFrom(this.store.select(selectApiConfigState)),
            filter(([_, state]) => !state.preloadProgress.done),
            map(() => this.apiConfigStorageService.getApiConfigFromStorage()),
            switchMap(({ identifier, apiVersion }) => {
                if (isValidIdentifier(identifier)) {
                    return [preloadApiConfigSuccess({ identifier, apiVersion }), refreshApiVersion()];
                }

                return [preloadApiConfigSuccess({ identifier, apiVersion })];
            }),
        ),
    );

    registerApiConfigs$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(registerApiConfig),
            switchMap(({ identifier }) => {
                if (!isValidIdentifier(identifier))
                    return of(registerApiConfigFailure({ error: InvalidIdentifierError }));
                return this.terminalClient.getApiVersion(identifier).pipe(
                    map((apiVersion) =>
                        isValidApiVersion(apiVersion)
                            ? registerApiConfigSuccess({
                                  identifier,
                                  apiVersion,
                              })
                            : registerApiConfigFailure({ error: InvalidApiVersionError }),
                    ),
                    catchError((error) => of(registerApiConfigFailure({ error: error?.toString() }))),
                );
            }),
        );
    });

    refreshApiVersion$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(refreshApiVersion),
            withLatestFrom(this.store.select(selectIdentifier)),
            switchMap(([, identifier]) => {
                if (!isValidIdentifier(identifier)) return of(refreshApiVersionFailure());
                return this.terminalClient.getApiVersion(identifier).pipe(
                    map((apiVersion) => {
                        return isValidApiVersion(apiVersion)
                            ? refreshApiVersionSuccess({ apiVersion })
                            : resetApiConfig();
                    }),
                    catchError(() => of(refreshApiVersionFailure())),
                );
            }),
        );
    });

    putApiVersionToStorage$ = createEffect(
        () =>
            this.actions$.pipe(
                ofType(refreshApiVersionSuccess),
                map(({ apiVersion }) => {
                    this.apiConfigStorageService.putApiVersionToStorage(apiVersion);
                    return true;
                }),
            ),
        { dispatch: false },
    );

    putApiConfigToStorage$ = createEffect(
        () =>
            this.actions$.pipe(
                ofType(registerApiConfigSuccess),
                map((apiConfig) => {
                    this.apiConfigStorageService.putApiConfigToStorage(apiConfig);
                    return true;
                }),
            ),
        { dispatch: false },
    );

    clearApiConfigFromStorage$ = createEffect(
        () =>
            this.actions$.pipe(
                ofType(resetApiConfig),
                map(() => {
                    this.apiConfigStorageService.clearApiConfigFromStorage();
                    return true;
                }),
            ),
        { dispatch: false },
    );

    constructor(
        private actions$: Actions,
        private store: Store,
        private apiConfigStorageService: ApiConfigStorageService,
        private terminalClient: TerminalClient,
    ) {}
}
